/**
 * Per-request routing engine override, driven by a URL parameter.
 *
 * Drive-time search is being moved from Valhalla isochrones to `reachd`, a
 * contraction-hierarchy service. The backend accepts an `engine` field on the
 * search request, defaulting to `valhalla`, so the switch is opt-in per request
 * rather than a deployed cutover: there is nothing to roll back, because not
 * asking for it IS the default.
 *
 * This exposes that switch to the client so it can be exercised by hand:
 *
 *   ?engine=reachd     use the contraction hierarchy, and its own polygon
 *   ?engine=valhalla   back to the current behaviour, and clear the override
 *
 * The choice is remembered in sessionStorage so it survives in-app navigation
 * that drops the query string — otherwise the first map move would silently
 * revert to Valhalla and make a comparison impossible to hold still. It is
 * sessionStorage rather than localStorage on purpose: closing the tab ends the
 * experiment, so nobody is left pinned to a non-default engine indefinitely.
 */

export type RoutingEngine = "valhalla" | "reachd";

const STORAGE_KEY = "iwc:routingEngine";
const VALID: RoutingEngine[] = ["valhalla", "reachd"];

function readStored(): RoutingEngine | undefined {
  try {
    const v = window.sessionStorage.getItem(STORAGE_KEY);
    return VALID.includes(v as RoutingEngine) ? (v as RoutingEngine) : undefined;
  } catch {
    // Private browsing and some embedded webviews throw on storage access.
    return undefined;
  }
}

function store(engine: RoutingEngine | undefined) {
  try {
    if (engine === undefined) window.sessionStorage.removeItem(STORAGE_KEY);
    else window.sessionStorage.setItem(STORAGE_KEY, engine);
  } catch {
    /* not fatal: the override just will not survive navigation */
  }
}

/**
 * The engine to use, or undefined to let the backend default apply.
 *
 * A URL parameter always wins over the stored value, so a link can override a
 * session that was already switched. `?engine=valhalla` clears the override
 * rather than pinning it, which makes going back to normal a URL edit instead
 * of a cache clear.
 */
export function getRoutingEngine(): RoutingEngine | undefined {
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get("engine");
  } catch {
    fromUrl = null;
  }

  if (fromUrl && VALID.includes(fromUrl as RoutingEngine)) {
    const engine = fromUrl as RoutingEngine;
    // valhalla is the backend default, so asking for it means "stop overriding"
    // rather than "pin me to valhalla".
    store(engine === "valhalla" ? undefined : engine);
    return engine === "valhalla" ? undefined : engine;
  }

  return readStored();
}

/** True when a non-default engine is active, for showing an indicator. */
export function isEngineOverridden(): boolean {
  return getRoutingEngine() !== undefined;
}
